import { styled, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useState } from 'react';

const animated = keyframes`
  25% {
    rotate: 90deg;
  }

  50% {
    rotate: 180deg;
  }

  75% {
    rotate: 270deg;
  }

  100% {
    rotate: 360deg;
  }
`;

const GroupAnimated = styled('g')({
  transformOrigin: '50% 60%',
  animation: `${animated} 4s linear infinite`,
  animationPlayState: 'var(--animationPlayState)',
});

const StyledSvg = styled('svg')({
  '--animationPlayState': 'paused',
  '&.play': {
    '--animationPlayState': 'running',
  },
});
export type AiBackgroundRef = {
  play: () => void;
  pause: () => void;
};
export type AiBackgroundProps = {
  className?: string;
  disableAutoplay?: boolean;
};

export const AiBackground = forwardRef<AiBackgroundRef, AiBackgroundProps>(
  ({ className, disableAutoplay = false }, ref) => {
    const theme = useTheme();

    const [play, setPlay] = useState(() => !disableAutoplay);

    useImperativeHandle(
      ref,
      () => ({
        play: () => setPlay(true),
        pause: () => setPlay(false),
      }),
      [],
    );

    return (
      <StyledSvg
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={classNames(className, { play })}
      >
        <g clipPath="url(#clip0_129_858)">
          <rect width="40" height="40" fill="#252525" />
          <GroupAnimated filter="url(#filter0_f_129_858)">
            <path
              d="M3.91843 -4.29288C0.722532 -9.56187 2.16585 -15.026 12.1659 -20.1974C21.032 -21.8562 24.3104 -20.334 28.764 -16.1969C34.3311 -11.0255 39.0734 -5.95164 41.3414 4.09847C43.6095 14.1486 37.527 21.8569 30.5166 26.638C23.5062 31.4192 11.9597 20.686 10.4133 10.9286C8.86693 1.17126 7.11434 0.97611 3.91843 -4.29288Z"
              fill={theme.palette.communicate[500]}
            />
            <path
              d="M38.6814 22.3409C46.6455 22.048 49.7377 27.3447 50.4464 33.8765C50.7842 34.8451 51.1477 37.5036 49.8991 40.3879C48.3383 43.9934 43.2548 43.6844 39.1944 47.1014C35.134 50.5183 44.3909 56.3544 30.5248 56.7234C16.6587 57.0924 22.1506 45.8806 9.75319 39.063C-2.64419 32.2454 8.49162 18.6492 22.1386 17.1467C32.2639 16.0319 30.9311 22.626 38.6814 22.3409Z"
              fill={theme.palette.communicate[500]}
            />
            <path
              d="M13.7699 56.62C11.0481 62.0792 5.52542 64.0689 -4.60133 59.2887C-11.0608 53.3884 -11.611 49.9937 -10.5772 44.2067C-9.28502 36.973 -7.60874 30.4312 -0.244692 22.9724C7.11936 15.5136 17.2066 15.8295 25.3007 18.5241C33.3948 21.2187 30.7084 36.2262 23.1868 42.9618C15.6652 49.6974 16.4918 51.1608 13.7699 56.62Z"
              fill={theme.palette.collaborate[500]}
            />
            <path
              d="M11.2643 -13.9771C17.4543 -14.2952 19.8115 -9.66918 20.3064 -3.94073C20.5606 -3.09296 20.8203 -0.762039 19.8256 1.77955C18.5822 4.95653 14.6353 4.72427 11.4515 7.75491C8.2677 10.7855 15.4098 15.8378 4.63374 16.268C-6.14234 16.6982 -1.77984 6.81385 -11.3535 0.924005C-20.9271 -4.96584 -12.1593 -16.9866 -1.54386 -18.4101C6.33228 -19.4662 5.24054 -13.6675 11.2643 -13.9771Z"
              fill={theme.palette.drive[500]}
            />
          </GroupAnimated>
        </g>
        <defs>
          <filter
            id="filter0_f_129_858"
            x="-34.5391"
            y="-39.9327"
            width="104.385"
            height="120.997"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="9.53804"
              result="effect1_foregroundBlur_129_858"
            />
          </filter>
          <clipPath id="clip0_129_858">
            <rect width="40" height="40" fill="white" />
          </clipPath>
        </defs>
      </StyledSvg>
    );
  },
);
